import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, Edit2, UserX, UserCheck, KeyRound, ArrowRightLeft } from "lucide-react";
import { useConfig, ALL_CONFIG_GROUPS } from "@/hooks/useConfig";

import { useAuth } from "@/contexts/AuthContext";

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const isAdmin = (currentUser?.roles || []).includes("Admin") || (currentUser?.roles || []).includes("admin");
  const { getValues } = useConfig(ALL_CONFIG_GROUPS);
  const DEPARTMENTS = getValues("departments");
  const [users, setUsers] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);   // FX1 D1
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "", department: "", roles: [], reports_to: "", assigned_channels: [], team_id: "", warehouse_id: "" });
  const [channels, setChannels] = useState([]);
  const [allWarehouses, setAllWarehouses] = useState([]);
  const [allUsersForReportsTo, setAllUsersForReportsTo] = useState([]);
  const [showResetPw, setShowResetPw] = useState(false);
  const [resetPwUser, setResetPwUser] = useState(null);
  const [resetPwForm, setResetPwForm] = useState({ new_password: "", confirm: "" });
  const [resetPwError, setResetPwError] = useState("");
  const [resetPwSaving, setResetPwSaving] = useState(false);

  // Transfer Channels state
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferFrom, setTransferFrom] = useState("");
  const [transferTo, setTransferTo] = useState("");
  const [transferring, setTransferring] = useState(false);

  const loadData = async () => {
    try {
      const [usersRes, rolesRes, chRes] = await Promise.all([
        api.get("/users", { params: { search: search || undefined, include_deleted: showDeleted || undefined } }),
        api.get("/roles"),
        api.get("/channels"),
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
      setChannels(chRes.data);
      if (allWarehouses.length === 0) { api.get("/warehouses").then(r => setAllWarehouses(r.data || [])).catch(e => console.error("API error:", e)); }
      // Load ALL users (unfiltered) for the Reports To dropdown
      if (allUsersForReportsTo.length === 0) {
        const { data: allU } = await api.get("/users");
        setAllUsersForReportsTo(allU);
      }
    } catch(e) { console.error("Load error:", e); } finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, [search, showDeleted]);

  const openCreate = () => {
    setEditingUser(null);
    setForm({ email: "", password: "", name: "", phone: "", department: "", roles: [], reports_to: "", assigned_channels: [], team_id: "" });
    setShowDialog(true);
  };

  const openEdit = (u) => {
    setEditingUser(u);
    setForm({ email: u.email, password: "", name: u.name, phone: u.phone || "", department: u.department || "", roles: u.roles || [], reports_to: u.reports_to || "", assigned_channels: u.assigned_channels || [], team_id: u.team_id || "", warehouse_id: u.warehouse_id || "" });
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      if (editingUser) {
        const updateData = { name: form.name, phone: form.phone, department: form.department, roles: form.roles, assigned_channels: form.assigned_channels, team_id: form.team_id, reports_to: form.reports_to || "", warehouse_id: form.warehouse_id || "" };
        await api.put(`/users/${editingUser._id}`, updateData);
        toast.success("User updated");
      } else {
        await api.post("/users", form);
        toast.success("User created");
      }
      setShowDialog(false);
      loadData();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const handleDeactivate = async (userId) => {
    if (!window.confirm("Deactivate this user?")) return;
    try {
      await api.delete(`/users/${userId}`);
      toast.success("User deactivated");
      loadData();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="users-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950">Users</h1>
          <p className="text-sm text-zinc-500 mt-1">Manage system users and their roles</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button variant="outline" onClick={() => { setTransferFrom(""); setTransferTo(""); setShowTransfer(true); }} className="rounded-sm h-8 text-xs" data-testid="transfer-channels-btn">
              <ArrowRightLeft size={14} /> Transfer Channels
            </Button>
          )}
          <Button onClick={openCreate} className="rounded-sm h-8 text-xs" data-testid="create-user-button">
            <Plus size={14} /> Add User
          </Button>
        </div>
      </div>

      <div className="mb-4 flex items-center gap-4">
        <div className="relative w-full max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            placeholder="Search users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-8 text-xs rounded-sm"
            data-testid="users-search-input"
          />
        </div>
        {isAdmin && (
          <label className="flex items-center gap-2 text-xs text-zinc-600 cursor-pointer select-none" data-testid="show-deleted-users-label">
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
              className="h-3.5 w-3.5 accent-zinc-900"
              data-testid="show-deleted-users-toggle"
            />
            Show deleted users
          </label>
        )}
      </div>

      <div className="border border-zinc-200 overflow-hidden">
        <table className="data-table w-full" data-testid="users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Channels</th>
              <th>Status</th>
              <th className="w-20">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-8 text-zinc-400 text-xs">Loading...</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-8 text-zinc-400 text-xs">No users found</td></tr>
            ) : users.map((u) => (
              <tr key={u._id} data-testid={`user-row-${u._id}`}>
                <td className="font-medium text-zinc-900">{u.name}</td>
                <td className="font-mono text-xs">{u.email}</td>
                <td>
                  <div className="flex gap-1 flex-wrap">
                    {(u.roles || []).map((r) => {
                      const colors = {"Admin":"bg-zinc-900 text-white","Sales Manager":"bg-blue-600 text-white","Sales Executive":"bg-green-600 text-white","Finance Manager":"bg-violet-600 text-white","Finance Executive":"bg-purple-500 text-white","Warehouse Manager":"bg-orange-600 text-white","Warehouse Executive":"bg-orange-400 text-white"};
                      return <span key={r} className={`inline-block px-1.5 py-0.5 text-[10px] font-semibold rounded-sm ${colors[r] || 'bg-zinc-100 text-zinc-700'}`}>{r}</span>;
                    })}
                  </div>
                </td>
                <td>
                  {(u.assigned_channels || []).length > 0 ? (
                    <span className="text-[10px] text-zinc-500">{u.assigned_channels.length} channel{u.assigned_channels.length > 1 ? 's' : ''}</span>
                  ) : <span className="text-[10px] text-zinc-400">-</span>}
                </td>
                <td>
                  <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold rounded-sm ${u.is_deleted ? 'bg-red-100 text-red-700' : u.status === 'active' ? 'status-active' : 'status-inactive'}`}>
                    {u.is_deleted ? 'deleted' : u.status}
                  </span>
                </td>
                <td>
                  <div className="flex gap-1">
                    {u.is_deleted ? (
                      isAdmin && (
                        <button onClick={async () => {
                          if (!window.confirm(`Restore ${u.name}? The user comes back as Inactive. Then set a password and activate the user.`)) return;
                          try { const { data } = await api.post(`/users/${u._id}/restore`); toast.success(data.message); loadData(); }
                          catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                        }} className="px-2 py-0.5 text-[10px] font-semibold border border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-sm" title="Restore" data-testid={`restore-user-${u._id}`}>
                          Restore
                        </button>
                      )
                    ) : (
                    <>
                    <button onClick={() => openEdit(u)} className="p-1 hover:bg-zinc-100 rounded-sm" data-testid={`edit-user-${u._id}`}>
                      <Edit2 size={13} className="text-zinc-500" />
                    </button>
                    {u.status === 'active' && (
                      <button onClick={() => handleDeactivate(u._id)} className="p-1 hover:bg-red-50 rounded-sm" data-testid={`deactivate-user-${u._id}`}>
                        <UserX size={13} className="text-red-500" />
                      </button>
                    )}
                    {u.status === 'inactive' && isAdmin && (
                      <button onClick={async () => {
                        if (!window.confirm(`Reactivate ${u.name}? They will be able to log in again with their existing email and password.`)) return;
                        try { await api.put(`/users/${u._id}`, { status: "active" }); toast.success(`${u.name} reactivated`); loadData(); }
                        catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                      }} className="p-1 hover:bg-green-50 rounded-sm" title="Reactivate" data-testid={`reactivate-user-${u._id}`}>
                        <UserCheck size={13} className="text-green-600" />
                      </button>
                    )}
                    {isAdmin && (
                      <button onClick={() => { setResetPwUser(u); setResetPwForm({ new_password: "", confirm: "" }); setResetPwError(""); setShowResetPw(true); }} className="p-1 hover:bg-amber-50 rounded-sm" title="Reset Password" data-testid={`reset-pw-${u._id}`}>
                        <KeyRound size={13} className="text-amber-600" />
                      </button>
                    )}
                    </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingUser ? "Edit User" : "Create User"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="mt-1 rounded-sm" data-testid="user-name-input" />
            </div>
            {!editingUser && (
              <>
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="mt-1 rounded-sm" data-testid="user-email-input" />
                </div>
                <div>
                  <Label className="text-xs">Password</Label>
                  <Input type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} className="mt-1 rounded-sm" data-testid="user-password-input" />
                </div>
              </>
            )}
            <div>
              <Label className="text-xs">Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="mt-1 rounded-sm" data-testid="user-phone-input" />
            </div>
            <div>
              <Label className="text-xs">Department</Label>
              <Select value={form.department || "none"} onValueChange={(v) => setForm({...form, department: v === "none" ? "" : v})}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="user-department-input"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent><SelectItem value="none">-- Select --</SelectItem>{DEPARTMENTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Roles (multi-select)</Label>
              <div className="mt-1 border border-zinc-200 rounded-sm p-2 max-h-40 overflow-y-auto" data-testid="user-role-select">
                {roles.filter(r => !r.is_future).map((r) => (
                  <label key={r._id} className="flex items-center gap-2 py-0.5 cursor-pointer text-xs">
                    <input type="checkbox" checked={form.roles.includes(r.name)} onChange={(e) => {
                      if (e.target.checked) setForm({...form, roles: [...form.roles, r.name]});
                      else setForm({...form, roles: form.roles.filter(x => x !== r.name)});
                    }} className="rounded" />
                    {r.name}
                  </label>
                ))}
              </div>
              {form.roles.length > 0 && <p className="text-[10px] text-zinc-500 mt-1">Selected: {form.roles.join(", ")}</p>}
            </div>
            {(form.roles.includes("Sales Executive") || form.roles.includes("Sales Manager")) && (
              <div>
                <Label className="text-xs">Assigned Sales Channels <span className="text-zinc-400">(Admin only)</span></Label>
                <div className="mt-1 border border-zinc-200 rounded-sm max-h-32 overflow-y-auto">
                  {channels.filter(c => c.status === "active").map(ch => (
                    <label key={ch._id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-zinc-50 cursor-pointer border-b border-zinc-50 last:border-0">
                      <input type="checkbox" checked={form.assigned_channels.includes(ch._id)} onChange={() => {
                        const chs = form.assigned_channels.includes(ch._id) ? form.assigned_channels.filter(id => id !== ch._id) : [...form.assigned_channels, ch._id];
                        setForm({...form, assigned_channels: chs});
                      }} className="rounded-sm" data-testid={`channel-assign-${ch._id}`} />
                      <span className="text-xs text-zinc-700">{ch.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs">Reports To</Label>
              <Select value={form.reports_to || "none"} onValueChange={(v) => setForm({...form, reports_to: v === "none" ? "" : v})}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="user-reportsto-select">
                  <SelectValue placeholder="Select manager" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- None --</SelectItem>
                  {allUsersForReportsTo.filter(u => u._id !== editingUser?._id).map((u) => (
                    <SelectItem key={u._id} value={u._id}>{u.name} ({(u.roles||[])[0]})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Assigned Warehouse</Label>
              <Select value={form.warehouse_id || "none"} onValueChange={(v) => setForm({...form, warehouse_id: v === "none" ? "" : v})}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="user-warehouse-select"><SelectValue placeholder="All warehouses" /></SelectTrigger>
                <SelectContent><SelectItem value="none">-- All Warehouses --</SelectItem>{allWarehouses.map(wh => <SelectItem key={wh._id} value={wh._id}>{wh.name}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-[9px] text-zinc-400 mt-0.5">Warehouse staff see only their warehouse's orders. Leave blank for cross-warehouse roles.</p>
            </div>

          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="rounded-sm text-xs">Cancel</Button>
            <Button onClick={handleSave} className="rounded-sm text-xs" data-testid="save-user-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Reset Password Dialog */}
      <Dialog open={showResetPw} onOpenChange={setShowResetPw}>
        <DialogContent className="max-w-sm" data-testid="reset-pw-dialog">
          <DialogHeader><DialogTitle className="font-heading">Reset Password</DialogTitle></DialogHeader>
          <p className="text-xs text-zinc-500">For: <span className="font-semibold">{resetPwUser?.name}</span> ({resetPwUser?.email})</p>
          <p className="text-[10px] text-amber-600 bg-amber-50 p-2 rounded-sm border border-amber-200">This user will be required to change their password on next login.</p>
          {resetPwError && <div className="p-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded-sm">{resetPwError}</div>}
          <div className="space-y-3">
            <div><Label className="text-xs">New Password</Label><Input type="password" value={resetPwForm.new_password} onChange={e => setResetPwForm({...resetPwForm, new_password: e.target.value})} className="mt-1 rounded-sm" data-testid="admin-reset-new-pw" /></div>
            <div><Label className="text-xs">Confirm New Password</Label><Input type="password" value={resetPwForm.confirm} onChange={e => setResetPwForm({...resetPwForm, confirm: e.target.value})} className="mt-1 rounded-sm" data-testid="admin-reset-confirm-pw" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetPw(false)} className="rounded-sm text-xs">Cancel</Button>
            <Button onClick={async () => {
              setResetPwError("");
              if (resetPwForm.new_password !== resetPwForm.confirm) { setResetPwError("Passwords do not match"); return; }
              if (resetPwForm.new_password.length < 8) { setResetPwError("Password must be at least 8 characters"); return; }
              setResetPwSaving(true);
              try {
                await api.post(`/users/${resetPwUser._id}/reset-password`, { new_password: resetPwForm.new_password });
                toast.success(`Password reset for ${resetPwUser.name}`);
                setShowResetPw(false);
              } catch (err) { setResetPwError(formatApiError(err.response?.data?.detail) || "Failed"); }
              finally { setResetPwSaving(false); }
            }} disabled={resetPwSaving} className="rounded-sm text-xs" data-testid="admin-reset-submit">{resetPwSaving ? "Saving..." : "Reset Password"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Channels Dialog */}
      <Dialog open={showTransfer} onOpenChange={setShowTransfer}>
        <DialogContent className="max-w-md" data-testid="transfer-channels-dialog">
          <DialogHeader><DialogTitle className="font-heading">Transfer Channels</DialogTitle></DialogHeader>
          {(() => {
            const activeUsers = (allUsersForReportsTo.length > 0 ? allUsersForReportsTo : users).filter(u => u.status === "active");
            const fromUser = activeUsers.find(u => u._id === transferFrom);
            const toUser = activeUsers.find(u => u._id === transferTo);
            const fromChannels = fromUser?.assigned_channels || [];
            const toCurrentChannels = toUser?.assigned_channels || [];
            const channelNames = fromChannels.map(chId => { const ch = channels.find(c => c._id === chId); return ch?.name || chId; });
            return (
              <div className="space-y-4">
                <div>
                  <Label className="text-xs">From User</Label>
                  <Select value={transferFrom || "none"} onValueChange={v => { setTransferFrom(v === "none" ? "" : v); if (v === transferTo) setTransferTo(""); }}>
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="transfer-from-select"><SelectValue placeholder="Select user" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">-- Select --</SelectItem>{activeUsers.map(u => <SelectItem key={u._id} value={u._id}>{u.name} ({(u.assigned_channels||[]).length} channels)</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">To User</Label>
                  <Select value={transferTo || "none"} onValueChange={v => setTransferTo(v === "none" ? "" : v)}>
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="transfer-to-select"><SelectValue placeholder="Select user" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">-- Select --</SelectItem>{activeUsers.filter(u => u._id !== transferFrom).map(u => <SelectItem key={u._id} value={u._id}>{u.name} ({(u.assigned_channels||[]).length} channels)</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {fromUser && toUser && (
                  <div className="border border-zinc-200 rounded-sm p-3 text-xs space-y-2" data-testid="transfer-preview">
                    <div className="flex justify-between"><span className="text-zinc-500">From:</span><span className="font-semibold">{fromUser.name} ({fromChannels.length} channels)</span></div>
                    <div className="flex justify-between"><span className="text-zinc-500">To:</span><span className="font-semibold">{toUser.name} (currently {toCurrentChannels.length} channels)</span></div>
                    <div className="flex justify-between text-blue-700 font-semibold"><span>After transfer:</span><span>{toUser.name} will have {fromChannels.length} channels</span></div>
                    {fromChannels.length > 0 && (
                      <div className="mt-2 border-t border-zinc-100 pt-2">
                        <p className="text-[10px] text-zinc-400 mb-1">Channels being transferred:</p>
                        <div className="flex flex-wrap gap-1">{channelNames.map(n => <span key={n} className="px-1.5 py-0.5 text-[9px] bg-zinc-100 text-zinc-700 rounded-sm">{n}</span>)}</div>
                      </div>
                    )}
                    <p className="text-[9px] text-amber-600 mt-1">This will REPLACE {toUser.name}'s current channel assignments.</p>
                  </div>
                )}
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransfer(false)} className="rounded-sm text-xs">Cancel</Button>
            <Button disabled={!transferFrom || !transferTo || transferring} onClick={async () => {
              setTransferring(true);
              try {
                const { data } = await api.put("/users/transfer-channels", { from_user_id: transferFrom, to_user_id: transferTo });
                toast.success(`Channels transferred to ${data.name}`);
                setShowTransfer(false);
                loadData();
              } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
              finally { setTransferring(false); }
            }} className="rounded-sm text-xs" data-testid="confirm-transfer-btn">{transferring ? "Transferring..." : "Confirm Transfer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
